import * as THREE from 'three';
import { BaseLayouter } from './BaseLayouter';
import { LayoutData, SpacingMode } from '../LayoutData';
import { POI } from '../../poi/POI';
import { Vector3 } from '../../types/Vector3';
import { Quaternion } from '../../types/Quaternion';

/**
 * MapAnchorLayouter
 *
 * Positions POI pins relative to a map POI (e.g., a GLTF/USDZ landkarte).
 *
 * Configuration via LayoutData.layouterProperties:
 * - mapName: string (name of the POI used as map anchor). If omitted, the first GLTF/USDZ POI is used.
 * - pinMode: 'local' | 'world' (default 'local')
 *   - local: read per-pin local offsets from poidata.localTranslationOffset and transform into world using map's Object3D.
 *   - world: preserve existing poidata.position as-is (no-op positioning; useful when pins already have absolute positions).
 *
 * NOTE: This is a minimal first version. Dedicated pin fields (pinLocal, pinUV, pinLat/Lon) will be added in a follow-up.
 */
export class MapAnchorLayouter extends BaseLayouter {
    constructor() {
        super(
            'map-anchor',
            'Positions pins relative to a map POI using local offsets or future UV/geo anchors',
            {
                mapName: '',
                pinMode: 'local'
            },
            '2d',
            [SpacingMode.Fixed]
        );
    }

    // Legacy calculate* API: return identity transforms
    calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 { return new Vector3(0, 0, 0); }
    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 { return new Vector3(0, 0, 0); }

    protected calculateOrientationQuaternion(position: Vector3, index: number, totalCount: number, layoutData: LayoutData): Quaternion {
        // Identity orientation (pins face +Z, world-up)
        return new Quaternion();
    }

    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        // Not used; return a small nominal spacing
        return 1;
    }

    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        // Not applicable; fall back to fixed
        return this.getFixedSpacingParameter(layoutData);
    }

    applyToObjects(pois: POI[], layoutData: LayoutData): void {
        if (!pois || pois.length === 0) return;

        const mapName = (layoutData.layouterProperties?.mapName as string) || '';
        const pinMode = (layoutData.layouterProperties?.pinMode as string) || 'local';

        // 1) Resolve map POI
        const mapPOI = this.findMapPOI(pois, mapName);
        if (!mapPOI) {
            // No anchor → nothing to do
            return;
        }

        // 2) Resolve map THREE.Object3D for coordinate transform
        const mapObj = mapPOI.getThreeObjects()?.[0] as THREE.Object3D | undefined;
        if (!mapObj) {
            return;
        }

        // Ensure world matrices are up to date
        mapObj.updateMatrixWorld(true);

        // 3) Position all non-map POIs
        for (const poi of pois) {
            if (poi === mapPOI) continue;

            if (pinMode === 'world') {
                // Respect existing absolute positions (no-op)
                continue;
            }

            // Default: local mode reads localTranslationOffset as a provisional pin-local
            const local = poi.poidata?.localTranslationOffset?.value;
            if (!local) continue;

            const localVec = new THREE.Vector3(local.x, local.y, local.z);
            const worldVec = localVec.clone();
            mapObj.localToWorld(worldVec);

            // Apply to POI data (world position)
            poi.poidata.position.setImmediate(new Vector3(worldVec.x, worldVec.y, worldVec.z));
        }
    }

    private findMapPOI(pois: POI[], preferredName: string): POI | null {
        if (preferredName) {
            const byName = pois.find(p => p.name === preferredName);
            if (byName) return byName;
        }

        // Fallback: choose first model-like POI (gltf/usdz), else the first POI
        const modelLike = pois.find(p => {
            try {
                const shape = p.poidata?.shape?.value?.toLowerCase?.();
                return shape === 'gltf' || shape === 'usdz' || shape === 'quader' || shape === 'tile';
            } catch { return false; }
        });
        // If only one POI exists (the map), we still return it so its transform can be stabilized.
        return modelLike || (pois.length === 1 ? pois[0] : null);
    }
}

